import React, { useEffect, useState, useMemo, useCallback } from "react";

import { useQuery, useMutation } from "@apollo/client";
import { Button, Container, Typography, Grid, Box } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import NavigationIcon from "@mui/icons-material/Navigation";
import MarkerIcon from "@mui/icons-material/Room";
import MapGL, { Marker } from "react-map-gl";
import { useParams, useNavigate } from "react-router-dom";
import { useSetRecoilState, useRecoilValue } from "recoil";

import { ONDELETETREE } from "api/mutations";
import { TREE } from "api/queries";
import { Tree } from "api/types";
import { LineLoading, Modal } from "components";
import { snackbarState, modalWaterState, geolocationState } from "state/atoms";
import { getDateToNow } from "utils/date";

import WazeImg from "./waze.png";
import { StyledButtonWaze, StyledButtonWazeImage, StyledLastGridItem, StyledDeleteButton } from "./DetailPage.styles";

type DetailPageParams = {
  id: string;
};

const DetailPage: React.FC = () => {
  const { id } = useParams<DetailPageParams>();
  const navigate = useNavigate();

  const [onDelete, { loading: deleteLoading }] = useMutation<{
    deleteTree: Tree;
  }>(ONDELETETREE);
  const { data, loading, refetch } = useQuery<{ tree: Tree }>(TREE, {
    fetchPolicy: "network-only",
    variables: {
      id,
    },
  });

  useEffect(() => {
    setInterval(() => {
      refetch();
    }, 60 * 1000);
  }, [refetch]);

  // State
  const [modalOpen, setModalOpen] = useState(false);
  const [vp, setVp] = useState({
    latitude: 0,
    longitude: 0,
    zoom: 16,
  });

  const { latitude, longitude } = useRecoilValue(geolocationState);
  const setModalState = useSetRecoilState(modalWaterState);
  const setSnackbarState = useSetRecoilState(snackbarState);

  useEffect(() => {
    if (data?.tree?.latitude && data?.tree?.longitude) {
      setModalState((p) => ({
        ...p,
        showNextId: true,
        id: data.tree.id,
      }));
      setVp((p) => ({
        ...p,
        latitude: data.tree.latitude,
        longitude: data.tree.longitude,
      }));
    }
    return () => {
      setModalState((p) => ({ ...p, showNextId: false }));
    };
  }, [data, setModalState]);

  const onRemove = useCallback(async () => {
    try {
      await onDelete({
        variables: {
          id,
        },
      });
      setModalOpen(false);
      navigate("/");
      setSnackbarState((p) => ({
        ...p,
        open: true,
        type: "success",
        message: "Removed",
      }));
    } catch (error) {
      setSnackbarState((p) => ({
        ...p,
        open: true,
        type: "error",
        message: "Ops! Something went wrong..",
      }));
    }
  }, [navigate, id, onDelete, setSnackbarState]);

  const onRouteGoogle = useCallback(() => {
    if (latitude && longitude) {
      window.location.href = `https://www.google.com/maps/dir/?api=1&origin=${latitude},${longitude}&destination=${vp.latitude},${vp.longitude}`;
    }
  }, [latitude, longitude, vp]);
  const onRouteWaze = useCallback(() => {
    if (vp.latitude && vp.longitude) {
      window.location.href = `https://www.waze.com/ul?ll=${vp.latitude},${vp.longitude}&navigate=yes&zoom=17`;
    }
  }, [vp]);

  const renderContent = useMemo(() => {
    if (data?.tree) {
      return (
        <>
          <MapGL
            {...vp}
            style={{ width: "100%", height: "40vh" }}
            mapStyle="mapbox://styles/mapbox/light-v9"
            mapboxAccessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
          >
            <Marker longitude={vp.longitude} latitude={vp.latitude}>
              <MarkerIcon />
            </Marker>
          </MapGL>
          <Container>
            <Grid container sx={{ mt: 3 }}>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <Box>
                    <Typography variant="h6">Address:</Typography>
                    <Typography>{data.tree.address}</Typography>
                    <Typography variant="h6">Last Time Watered:</Typography>
                    <Typography>
                      {data.tree.lastWateringAt ? getDateToNow(data.tree.lastWateringAt) : "No data"}
                    </Typography>
                    <StyledDeleteButton onClick={() => setModalOpen(true)} aria-label="delete">
                      <DeleteIcon />
                    </StyledDeleteButton>
                  </Box>
                </Box>
              </Grid>
              <StyledLastGridItem item xs={12} sm={6}>
                <Box display="flex" alignItems="center" justifyContent="center">
                  <Button onClick={onRouteGoogle} size="large" startIcon={<NavigationIcon fontSize="large" />}>
                    Maps
                  </Button>
                  <StyledButtonWaze onClick={onRouteWaze} sx={{ minWidth: 40, cursor: "pointer" }}>
                    <StyledButtonWazeImage src={WazeImg} alt="wazeicon" />
                  </StyledButtonWaze>
                </Box>
              </StyledLastGridItem>
            </Grid>
          </Container>
          <Modal
            open={modalOpen}
            onBack={() => setModalOpen(false)}
            loading={deleteLoading}
            title="Confirm Delete?"
            onSuccess={onRemove}
          />
        </>
      );
    }
    return null;
  }, [data, vp, deleteLoading, modalOpen, onRemove, onRouteGoogle, onRouteWaze]);

  return (
    <>
      <LineLoading show={loading} />
      {renderContent}
    </>
  );
};

export default DetailPage;
